import { useCallback, useEffect, useMemo, useState } from 'react';

import { useOutletContext } from 'react-router-dom';

import usePermissions from 'api/usePermissions';
import useReports from 'api/useReports';
import { useUserDetails } from 'api/useUserDetails';
import { ELocalStorageKeys } from 'constants/enums';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import { localStorageUtil } from 'utils/localStorageUtil';
import { permissionsUtil } from 'utils/permissionsUtil';
import { getSelectProjectOptions } from 'utils/projectsUtil';
import { EToasterStatus, showToast } from 'utils/showToast';

import DataTable from 'components/DataTableComponent/DataTable';
import InfoBanner from 'components/ui/InfoBanner';

import {
  FINANCIAL_REPORTS_SEARCH_PAGE_SIZE,
  FINANCIAL_REPORTS_TABLE_COLUMNS
} from '../constants/financialReportsTableConfig';
import { FinancialReportsOutletContext } from '../types/financialReports.types';
import { EReportStatusSearchOrder } from '../types/report.types';
import {
  normalizeSearchReportListItem,
  transformReportsToTableData
} from '../utils';

import { FinancialReportsEmptyState } from './financialReportsEmptyState';

const FinancialReportsTable = () => {
  const {
    sortDirection,
    selectedStatuses,
    selectedProjects,
    createdAfter,
    createdBefore
  } = useOutletContext<FinancialReportsOutletContext>();
  const { getPermissions } = usePermissions();
  const { userDetails } = useUserDetails();
  const projectsDetails = userDetails?.projects;
  const permissions = getPermissions();
  const allowedPublishers =
    permissionsUtil.getPublishersByFinancialReports(permissions);

  const [pageIndex, setPageIndex] = useState(0);

  const [showInfoBanner, setShowInfoBanner] = useState(() => {
    const savedState = localStorageUtil.get(
      ELocalStorageKeys.FINANCIAL_REPORTS_INFO_BANNER_CLOSED
    );
    return savedState === null;
  });

  const projectsHeader = useMemo(() => {
    if (!projectsDetails) {
      return undefined;
    }
    const projectOptions = getSelectProjectOptions(
      projectsDetails,
      allowedPublishers
    );
    if (projectOptions.length === 0) {
      return undefined;
    }
    const allowedPublisherIdSet = new Set(
      projectOptions.map((projectOption) => projectOption.value)
    );
    const selectedForSearch = selectedProjects.filter((publisherId) =>
      allowedPublisherIdSet.has(publisherId)
    );
    const effectivePublisherIds =
      selectedForSearch.length > 0
        ? selectedForSearch
        : projectOptions.map((projectOption) => projectOption.value);
    return effectivePublisherIds.join(',');
  }, [projectsDetails, allowedPublishers, selectedProjects]);

  const orderedStatuses = useMemo(
    () =>
      EReportStatusSearchOrder.filter((status) =>
        selectedStatuses.includes(status)
      ),
    [selectedStatuses]
  );

  const searchRequest = useMemo(
    () => ({
      sortDirection,
      page: pageIndex + 1,
      limit: FINANCIAL_REPORTS_SEARCH_PAGE_SIZE,
      ...(orderedStatuses.length > 0 ? { statuses: orderedStatuses } : {}),
      ...(createdAfter && createdBefore
        ? {
            createdAfter: dayjs(createdAfter).startOf('day').toISOString(),
            createdBefore: dayjs(createdBefore).endOf('day').toISOString()
          }
        : {})
    }),
    [pageIndex, sortDirection, orderedStatuses, createdAfter, createdBefore]
  );

  const searchRequestResetKey = useMemo(
    () =>
      `${sortDirection}-${selectedProjects.join(',')}-${orderedStatuses.join(',')}-${createdAfter}-${createdBefore}`,
    [sortDirection, selectedProjects, orderedStatuses, createdAfter, createdBefore]
  );

  const { getReportDownload, searchReports } = useReports({
    searchRequest,
    search: {
      projects: projectsHeader,
      enabled: Boolean(projectsHeader)
    }
  });

  useEffect(() => {
    setPageIndex(0);
  }, [searchRequestResetKey, projectsHeader]);

  const onDownloadClick = useCallback(
    (reportId: string) => {
      getReportDownload(reportId);
      showToast({
        message: 'Report download started',
        status: EToasterStatus.SUCCESS
      });
    },
    [getReportDownload]
  );

  const handleCloseInfoBanner = () => {
    setShowInfoBanner(false);
    localStorageUtil.set(
      ELocalStorageKeys.FINANCIAL_REPORTS_INFO_BANNER_CLOSED,
      'true'
    );
  };

  const columns = useMemo(() => {
    return FINANCIAL_REPORTS_TABLE_COLUMNS(onDownloadClick);
  }, [onDownloadClick]);

  const tableData = useMemo(() => {
    const reportsPayload = searchReports.data?.data;
    if (!reportsPayload?.length) {
      return [];
    }
    const asReportApi = reportsPayload.map((item) =>
      normalizeSearchReportListItem(item)
    );
    return transformReportsToTableData(
      asReportApi,
      projectsDetails,
      allowedPublishers
    );
  }, [searchReports.data, projectsDetails, allowedPublishers]);

  const pageSize = FINANCIAL_REPORTS_SEARCH_PAGE_SIZE;
  const response = searchReports.data;
  const totalCount = response?.totalCount ?? response?.total ?? 0;
  let pageCount = 0;
  if (response) {
    if (response.totalPages > 0) {
      pageCount = response.totalPages;
    } else {
      pageCount = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;
    }
  }

  const isEmptyState =
    !searchReports.isLoading && (totalCount === 0 || isEmpty(tableData));

  return (
    <>
      {showInfoBanner && (
        <InfoBanner
          onClose={handleCloseInfoBanner}
          message={
            <>
              The <b>finalized</b> financial data will be uploaded to the
              Payouts section after the end of each month
            </>
          }
        />
      )}
      {isEmptyState ? (
        <FinancialReportsEmptyState />
      ) : (
        <DataTable
          data={tableData}
          columns={columns}
          rowIdIdentifier="id"
          isLoading={searchReports.isLoading}
          rowsPerPage={pageSize}
          manualPagination
          pageIndex={pageIndex}
          pageCount={pageCount}
          onPageChange={setPageIndex}
          rowCount={totalCount}
          bordered
        />
      )}
    </>
  );
};

export default FinancialReportsTable;
